<template>
  <v-container
    v-if="child"
    class="py-4"
  >
    <v-btn
      @click="$router.push({ name: 'Dashboard' })"
      class="focus-very-visible px-0 mb-4"
      prepend-icon="west"
      variant="text"
    >
      {{ $bct('Back to dashboard') }}
    </v-btn>

    <v-card
      border
      flat
      tile
    >
      <v-card-title>
        <h1
          ref="title"
          class="d-flex align-center"
        >
          <v-avatar
            class="fs-28 c-white me-3"
            color="primary"
            size="60"
          >
            {{ [child.first_name[0], child.last_name[0]].join('').toUpperCase() }}
          </v-avatar>
          <div>
            <div
              v-text="[child.first_name, child.last_name].join(' ')"
              class="c-black fs-28"
              data-cy="child-title"
            />

            <div
              class="c-light-black fs-18"
              data-cy="child-dob"
            >
              <LongDate :date="child.dob" />
            </div>
          </div>
        </h1>
      </v-card-title>

      <v-card-text>
        <template v-if="schema.meta.parent_layout.applications_instructions">
          <v-card
            class="bg-primary mb-4"
            border
            flat
            tile
          >
            <v-card-text class="c-white fs-16 fw-500 markdown">
              <MarkdownContent :content="schema.meta.parent_layout.applications_instructions" />
            </v-card-text>
          </v-card>
        </template>

        <div
          v-for="subsidyProgram in childSubsidyPrograms"
          :key="subsidyProgram.id"
        >
          <MultipleSubsidiesCard
            v-if="subsidyProgram.enable_multiple_applications"
            :child-data="child"
            :child-schema="schema"
            :subsidies="
              subsidies.filter((subsidy) => subsidy.subsidy_program_id == subsidyProgram.id)
            "
            :subsidy-program="subsidyProgram"
          />
          <SubsidyCard
            v-else
            :child-data="child"
            :child-schema="schema"
            :subsidy="subsidies.find((subsidy) => subsidy.subsidy_program_id == subsidyProgram.id)"
            :subsidy-program="subsidyProgram"
          />
        </div>
      </v-card-text>
    </v-card>

    <VerticalSpacer :min-height="60" />
  </v-container>
</template>

<script setup>
import LongDate from '@/shared/components/LongDate.vue';
import MultipleSubsidiesCard from '@/parent/components/subsidy/MultipleSubsidiesCard.vue';
import SubsidyCard from '@/parent/components/subsidy/SubsidyCard.vue';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import { subsidyProgramIsOpen } from '@/parent/services/subsidies';

import useApi from '@/shared/composables/useApi';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const { api } = useApi();
const route = useRoute();
const store = useStore();
const $bct = inject('translator');

const child = ref(null);
const schema = ref(null);
const subsidies = ref([]);
const childSubsidyPrograms = ref([]);

async function loadChild() {
  const resp = await api.child.get(route.params.childId);
  child.value = resp.data;
  schema.value = store.state.schemas[child.value.schema_id];
}

async function load() {
  child.value = null;
  subsidies.value = [];
  childSubsidyPrograms.value = [];

  await loadChild();

  const subsidiesResp = await api.subsidy.index({ child_id: route.params.childId });

  subsidies.value = subsidiesResp.data;

  const subsidyProgramResp = await api.public_api.organization.subsidy_program.index({
    child_id: route.params.childId,
  });

  childSubsidyPrograms.value = subsidyProgramResp.data.filter(
    (childSubsidyProgram) =>
      subsidyProgramIsOpen(childSubsidyProgram) ||
      subsidies.value.find(
        (subsidy) => subsidy.subsidy_program_id === childSubsidyProgram.id && subsidy.submitted_at,
      ),
  );
}

watch(
  () => route.params.childId,
  async (newVal) => {
    if (newVal) {
      await load();
    }
  },
  { immediate: true },
);
</script>
